import { useState, useEffect, useContext, useRef, useMemo } from 'react'
import ContextStates from '../../context/ContextStates';
import { FaPen, FaTrash } from 'react-icons/fa'
import axios from 'axios';
import { Url } from '../Url';
import FooterVenta from '../footerVenta';
import SearchAutoComplete from '../searchAutoComplete';

// === Helpers básicos ===
const num = (v) => (typeof v === "number" ? v : Number.parseFloat(String(v || 0))) || 0;
const formatARS = (n) => Number(n).toLocaleString("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 2 });

// === COMBOS: agrupar cantidades por numerocombo ===
const buildQtyByCombo = (items, productById, productByCode) => {
    const map = {};
    for (const it of items || []) {
        // Venta puede no traer numerocombo: lo buscamos en el maestro por id o código
        const master =
            (it.id && productById[it.id]) ||
            (it.codigo && productByCode[String(it.codigo)]) ||
            null;
        const nc = master?.numerocombo;
        if (!nc) continue;
        const q = num(it.cantidad);
        map[nc] = (map[nc] || 0) + q;
    }
    return map;
};

// === COMBOS: unitario por producto usando tiers del producto + cantidad total del combo ===
const unitFromProductComboTiers = (base, productComboTiers, groupQty) => {
    let unit = num(base);
    const tiers = Array.isArray(productComboTiers)
        ? productComboTiers.slice().sort((a, b) => a.cantidadmin - b.cantidadmin)
        : [];
    for (const t of tiers) {
        if (groupQty >= Number(t.cantidadmin)) unit = num(t.precio_unitario);
        else break;
    }
    return unit;
};

const unitByCombo = (masterProduct, selfQty, qtyByCombo) => {
    const base = num(masterProduct?.precio ?? masterProduct?.precio_base ?? 0);
    if (masterProduct?.numerocombo && masterProduct?.precios_combo?.length) {
        const groupQty = qtyByCombo[masterProduct.numerocombo] || 0;
        return unitFromProductComboTiers(base, masterProduct.precios_combo, groupQty);
    }
    return base; // sin combo → base
};

const subtotalByCombo = (masterProduct, qty, qtyByCombo) => {
    const q = num(qty);
    if (q <= 0) return 0;
    const unit = unitByCombo(masterProduct, q, qtyByCombo);
    return unit * q;
};

// clave única por producto (preferimos id; si no, código)
const keyFor = (it) => String(it?.id ?? it?.codigo ?? "");

// mergea una lista de renglones sumando cantidades por el mismo producto
const mergeByProduct = (items = []) => {
    const map = new Map();
    for (const it of items) {
        const k = keyFor(it);
        if (!k) continue;
        if (map.has(k)) {
            const prev = map.get(k);
            map.set(k, { ...prev, cantidad: Number(prev.cantidad || 0) + Number(it.cantidad || 0) });
        } else {
            map.set(k, { ...it });
        }
    }
    return Array.from(map.values());
};

const FormVentas = () => {
    const [cantidad, setCantidad] = useState('')
    const [producto, setProducto] = useState({})
    const [combo, setCombo] = useState({})
    const [descripcion, setDescripcion] = useState('')
    const [precioUnitario, setPrecioUnitario] = useState('')

    const {
        productos, setProductos,
        combos, setCombos,
        codigo, setCodigo,
        venta, setVenta,
        setProductoSelected, setViewOpen,
        modify, setFormView, tipoPrecio,
        searchTerm, setSearchTerm,
        idUsuario
    } = useContext(ContextStates)

    const inputRef = useRef(null);
    const cantRef = useRef(null);
    const searchRef = useRef(null)

    useEffect(() => {
        const getProductos = async () => {
            const formData = new FormData()
            formData.append("idusuario", idUsuario)
            try {
                const results = await axios({
                    url: Url + 'api/listadoproductos.php',
                    method: 'POST',
                    data: formData,
                })
                // === Normalizar campos de combos por producto ===
                const arr = Array.isArray(results?.data?.results) ? results.data.results : [];
                const normalized = arr.map((it) => ({
                    ...it,
                    precio: num(it.precio),
                    preciocosto: num(it.preciocosto),
                    stock: Number(it.stock ?? 0),
                    numerocombo: it.numerocombo ?? null,
                    precios_combo: Array.isArray(it.precios_combo)
                        ? it.precios_combo
                            .map(t => ({ cantidadmin: Number(t.cantidadmin), precio_unitario: num(t.precio_unitario) }))
                            .sort((a, b) => a.cantidadmin - b.cantidadmin)
                        : [],
                }));
                setProductos(normalized);
            } catch (e) {
                console.log(e)
            }
        }
        getProductos()
    }, [modify, idUsuario, setProductos])

    /*useEffect(() => {
        const getCombos = async () => {
            const formData = new FormData()
            formData.append('id', 1);
            try {
                const results = await axios({
                    url: Url + 'api/listCombosVenta.php',
                    method: 'POST',
                    data: formData,
                })
                setCombos(results.data.results);
            } catch (e) {
                console.log(e)
            }
        }
        getCombos()
    }, [modify, setCombos])*/

    useEffect(() => {
        const addProducto = async () => {
            if (producto && producto.codigo) {
                const auxVenta = JSON.stringify(venta || []);
                const formData = new FormData();
                formData.append('venta', auxVenta);
                formData.append('producto', JSON.stringify(producto));
                try {
                    const { data } = await axios({
                        url: Url + 'api/addProducto.php',
                        method: 'POST',
                        data: formData,
                    });
                    const list = Array.isArray(data?.results) ? data.results : [];
                    setVenta(mergeByProduct(list)); // 👈 agrupado
                    setSearchTerm('');
                    setProducto({});
                    setCantidad('');
                    setPrecioUnitario('');
                } catch (e) {
                    console.log(e);
                }
            }
        };
        addProducto();
    }, [producto]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const addCombo = async () => {
            if (combo && combo.codigo) {
                const auxVenta = JSON.stringify(venta)
                const formData = new FormData()
                formData.append('venta', auxVenta);
                formData.append('combo', JSON.stringify(combo));
                try {
                    const response = await axios({
                        url: Url + 'api/addCombo.php',
                        'Content-Type': 'application/json',
                        method: 'POST',
                        data: formData,
                        dataType: 'JSON',
                    })
                    setVenta(response.data.results);
                    setCombo({})
                    setCantidad('')
                    setPrecioUnitario('')
                } catch (e) {
                    console.log(e)
                }
            }
        }
        addCombo()
    }, [combo]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const checkDatos = () => {
            productos.forEach(element => {
                if (String(element.codigo) === String(codigo)) {
                    setSearchTerm(element.descripcion)
                    // Mantengo tu lógica de tipoPrecio solo para completar el campo input "Precio/u" (no afecta combo)
                    if (tipoPrecio === 1) {
                        if (element.tipopromo === '1' || element.tipopromo === '2') {
                            setPrecioUnitario(element.preciopromocion)
                        }
                        else {
                            setPrecioUnitario(element.precio)
                        }
                    }
                    else {
                        setPrecioUnitario(element.preciopormayor)
                    }
                }
            });
            if (cantidad === '') {
                if (codigo === '') searchRef.current?.focus();
                else cantRef.current?.focus();
            }
        }
        if (codigo) checkDatos()
    }, [codigo, productos, tipoPrecio, cantidad, setSearchTerm])

    useEffect(() => {
        // Mantengo tu hook (no modifica cantidad automáticamente)
        const handleSetPrecio = () => {
            if (!codigo) return;
            // Podrías derivar cantidad desde precio si lo necesitás.
        }
        handleSetPrecio()
    }, [precioUnitario, codigo])

    const preAdd = async (event) => {
        event.preventDefault();
        if (cantidad !== '') {
            let aux = productos.find(element => String(element.codigo) === String(codigo));
            if (typeof aux !== 'undefined') {
                aux = { ...aux, cantidad, codigo };
                setProducto(aux);
            } else {
                let cx = combos.find(element => String(element.codigo) === String(codigo));
                if (typeof cx !== 'undefined') {
                    cx = { ...cx, cantidad };
                    setCombo(cx);
                }
            }
            setCodigo('')
        } else {
            cantRef.current?.focus()
        }
    }

    const submitCantidad = (event) => {
        event.preventDefault();
        if (cantidad !== '' && codigo !== '') {
            preAdd(event)
            searchRef.current?.focus()
        }
    }

    const handleAdd = (e) => {
        preAdd(e)
        searchRef.current?.focus()
    }

    const handleDelete = (item) => {
        const rData = (venta || []).filter(element => element.linea !== item.linea);
        setVenta(rData)
    }

    const handleSet = (item) => {
        setProductoSelected(item)
        setFormView(1)
        setViewOpen(true)
    }

    const calcTotal = (item) => {
        // (Se mantiene para compatibilidad con tu campo tipopromo==3)
        let precio = 0;
        if (item.cantidad >= item.cantidadminima) {
            let cantPromos = parseFloat(item.cantidad) / parseFloat(item.cantidadminima);
            let cantPagos = item.cantidad - (parseFloat(item.cantidaddescuento) * cantPromos);
            precio = cantPagos * parseFloat(item.precio);
        } else {
            precio = parseFloat(item.cantidad) * parseFloat(item.precio);
        }
        return (precio);
    }

    // =================== COMBOS: mapas maestro y cantidades por combo ===================
    const productById = useMemo(() => {
        const map = {};
        for (const p of productos || []) map[p.id] = p;
        return map;
    }, [productos]);

    const productByCode = useMemo(() => {
        const map = {};
        for (const p of productos || []) map[String(p.codigo)] = p;
        return map;
    }, [productos]);

    const qtyByCombo = useMemo(() => buildQtyByCombo(venta || [], productById, productByCode), [venta, productById, productByCode]);

    return (
        <div className='flex flex-col md:h-screen justify-between'>
            {/* Barra superior */}
            <div className="transition-all w-full mt-3 flex">
                <div className={`w-full md:w-11/12 lg:w-10/12 mx-auto mt-1 p-3 rounded-lg md:flex ${tipoPrecio === 1 ? 'bg-indigo-200' : tipoPrecio === 2 ? 'bg-orange-200' : 'bg-green-200'} `}>
                    <form className="w-1/4" onSubmit={preAdd}>
                        <div className="sm:items-start lg:px-5 sm:px-1">
                            <label htmlFor="codigo" className="block text-gray-600 uppercase font-bold">Código</label>
                            <input
                                type="text"
                                className="py-2 px-3 w-full rounded"
                                id="codigo"
                                ref={inputRef}
                                placeholder="Código de producto"
                                value={codigo}
                                // FIX: este era el bug
                                onChange={(e) => setCodigo(e.target.value)}
                                autoFocus
                            />
                        </div>
                    </form>

                    <div className="w-1/2 sm:items-start lg:px-5 sm:px-1">
                        <SearchAutoComplete
                            items={productos}
                            innerRef={searchRef}
                            // si querés autocompletar código al seleccionar:
                            onPick={(p) => { if (p?.codigo) { setCodigo(String(p.codigo)); setSearchTerm(p.descripcion || ''); cantRef.current?.focus(); } }}
                        />
                    </div>

                    <div className="w-1/10 sm:items-start lg:px-5 sm:px-1">
                        <label htmlFor="punitario" className="block text-gray-600 uppercase font-bold">Precio/u</label>
                        <input
                            type="text"
                            className="py-2 px-3 w-full rounded"
                            id="punitario"
                            value={precioUnitario}
                            onChange={(e) => setPrecioUnitario(e.target.value)}
                        />
                    </div>

                    <div className="w-1/10 sm:items-start lg:px-5 sm:px-1">
                        <form onSubmit={submitCantidad}>
                            <label htmlFor="cantidad" className="block text-gray-600 uppercase font-bold">Cantidad</label>
                            <input
                                type="number"
                                className="py-2 px-3 w-full rounded"
                                id="cantidad"
                                ref={cantRef}
                                placeholder="Cantidad"
                                value={cantidad}
                                onChange={(e) => setCantidad(e.target.value)}
                            />
                        </form>
                    </div>

                    <div className="w-1/10 sm:items-start px-5 pt-4">
                        <input
                            type="button"
                            className="w-full shadow-md p-3 bg-blue-600 text-gray-100 hover:bg-blue-500 hover:cursor-pointer transition-all uppercase font-semibold rounded mt-1"
                            value="Agregar"
                            onClick={(e) => handleAdd(e)}
                        />
                    </div>
                </div>
            </div>

            {/* Tabla */}
            <div className="w-full md:w-10/12 mx-auto mt-3 mb-20">
                {!!venta?.length && (
                    <table className='table-auto w-full border border-collapse border-gray-300'>
                        <thead>
                            <tr className='border border-collapse border-gray-300 bg-blue-100'>
                                <th className='border border-collapse border-gray-300'>Cantidad</th>
                                <th className='border border-collapse border-gray-300'>Código</th>
                                <th className='border border-collapse border-gray-300'>Descripción</th>
                                <th className='border border-collapse border-gray-300'>Precio</th>
                                <th className='border border-collapse border-gray-300'>Subtotal</th>
                                <th className='border border-collapse border-gray-300'>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {venta.map((item, index) => {
                                // MASTER del producto (para conocer numerocombo y precios_combo)
                                const master =
                                    (item.id && productById[item.id]) ||
                                    (item.codigo && productByCode[String(item.codigo)]) ||
                                    null;

                                // === COMBOS: unitario y subtotal calculados por combo/Producto ===
                                const unitCombo = unitByCombo(master, item.cantidad, qtyByCombo);
                                const subtotalCombo = subtotalByCombo(master, item.cantidad, qtyByCombo);

                                // Fallback de precio/subtotal si no hay info de combo ni master
                                const priceFallback =
                                    tipoPrecio === 2 ? Math.round(item.preciopormayor)
                                        : tipoPrecio === 3 ? Math.round(item.preciopormayor2)
                                            : (item.tipopromo === '1' || item.tipopromo === '2') ? Math.round(item.preciopromocion)
                                                : Math.round(item.precio);

                                const subtotalFallback =
                                    tipoPrecio === 2 ? (num(item.preciopormayor) * num(item.cantidad))
                                        : tipoPrecio === 3 ? (num(item.preciopormayor2) * num(item.cantidad))
                                            : (item.tipopromo === '1' || item.tipopromo === '2') ? (num(item.preciopromocion) * num(item.cantidad))
                                                : item.tipopromo === '3' ? calcTotal(item)
                                                    : (num(item.precio) * num(item.cantidad));

                                const showUnit = master ? unitCombo : priceFallback;
                                const showSubtotal = master ? subtotalCombo : subtotalFallback;

                                return (
                                    <tr
                                        key={index}
                                        className={`${index % 2 === 0 ? 'bg-white hover:bg-slate-200' : 'bg-slate-100 hover:bg-slate-200'} hover:cursor-pointer border-b border-gray-200`}
                                    >
                                        <td className='border border-collapse border-gray-300 px-3 text-center'>{item.cantidad}</td>
                                        <td className='border border-collapse border-gray-300 px-3 text-center'>{item.codigo}</td>
                                        <td className='border border-collapse border-gray-300 px-3'>
                                            {item.descripcion}
                                            {!!master?.numerocombo && (
                                                <div className="text-[11px] text-emerald-700 mt-1">
                                                    Combo #{String(master.numerocombo)}
                                                </div>
                                            )}
                                        </td>

                                        {/* === COMBOS: mostramos el unitario del combo si aplica === */}
                                        <td className='border border-collapse border-gray-300 px-3 text-right'>
                                            {formatARS(showUnit)}
                                        </td>

                                        {/* === COMBOS: mostramos subtotal calculado por combo si aplica === */}
                                        <td className='border border-collapse border-gray-300 px-3 text-right'>
                                            {formatARS(showSubtotal)}
                                        </td>

                                        <td className='border border-collapse border-gray-300 px-3 text-center'>
                                            <button
                                                className='bg-gray-400 p-3 rounded-md text-white ml-3'
                                                onClick={() => handleSet(item)}
                                            >
                                                <FaPen />
                                            </button>
                                            <button
                                                className='bg-red-400 p-3 rounded-md text-white ml-3'
                                                onClick={() => handleDelete(item)}
                                            >
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <FooterVenta />
        </div>
    )
}

export default FormVentas
